import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ChevronLeft, 
  Package as PackageIcon,
  ShoppingBag,
  DollarSign,
  Box,
  Loader,
  Info
} from 'lucide-react';
import { RecommendedProductsRow } from './RecommendedProductsRow'; // ← NEW

const API_URL = process.env.REACT_APP_API_URL;

function MyDelivery({ delivery, onBack }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackageDetails();
  }, [delivery.id]);

  const fetchPackageDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/delivery/${delivery.id}/packages`);
      setPackages(response.data.packages || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('No se pudo cargar la información de los paquetes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'in_warehouse': 'bg-blue-100 text-blue-800 border-blue-200',
      'in_transit': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'delivered': 'bg-green-100 text-green-800 border-green-200',
      'pending': 'bg-gray-100 text-gray-800 border-gray-200',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'in_warehouse': 'En almacén',
      'in_transit': 'En tránsito',
      'delivered': 'Entregado',
      'pending': 'Pendiente',
    };
    return statusTexts[status] || status;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200 active:scale-95"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <PackageIcon className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">
                  Mi Entrega
                </h1>
                <p className="text-xs text-white/90 font-montserrat">
                  Detalles de tus paquetes
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">

          {/* Delivery Summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 font-montserrat mb-1">
                  {delivery.recipient_name}
                </h2>
                <p className="text-sm text-slate-600 font-montserrat">
                  {delivery.address}
                </p>
              </div>
              {delivery.charge > 0 && (
                <div className="text-right">
                  <p className="text-xs text-slate-600 font-montserrat mb-1">Total a pagar</p>
                  <p className="text-2xl font-bold text-brand-primary font-montserrat">
                    S/ {delivery.charge.toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            {delivery.notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <div className="flex gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900 font-montserrat mb-1">
                      Nota de entrega
                    </p>
                    <p className="text-sm text-blue-800 font-montserrat">{delivery.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <Loader className="w-8 h-8 text-brand-primary animate-spin" />
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-800 font-montserrat">{error}</p>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && packages.length === 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
              <Box className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 font-montserrat">No hay paquetes disponibles</p>
            </div>
          )}

          {/* Packages List */}
          {!loading && !error && packages.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-4">
                Tus Paquetes ({packages.length})
              </h3>

              {packages.map((pkg, index) => (
                <div key={pkg.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  {/* Package Header */}
                  <div className="bg-gradient-to-r from-brand-primary/5 to-brand-gold/5 px-4 py-3 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-brand-primary" />
                        <span className="text-sm font-semibold text-slate-900 font-montserrat">
                          Paquete #{index + 1}
                        </span>
                      </div>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${getStatusColor(pkg.status)}`}>
                        {getStatusText(pkg.status)}
                      </span>
                    </div>
                    {pkg.tracking_code && (
                      <p className="text-xs text-slate-600 font-montserrat mt-1">
                        Código: {delivery.id}
                      </p>
                    )}
                  </div>

                  {/* Package Items */}
                  <div className="p-4">
                    {pkg.items && pkg.items.length > 0 ? (
                      <div className="space-y-3">
                        {pkg.items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0"
                          >
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-primary/10 to-brand-gold/10 flex items-center justify-center flex-shrink-0">
                              <Box className="w-5 h-5 text-brand-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-slate-900 font-montserrat mb-1">
                                {item.product?.title || 'Producto'}
                              </h4>
                              {item.product?.description && (
                                <p className="text-sm text-slate-600 font-montserrat mb-2 line-clamp-2">
                                  {item.product.description}
                                </p>
                              )}
                              <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-1 text-slate-700">
                                  <span className="font-semibold">Cantidad:</span>
                                  <span>{item.quantity}</span>
                                </div>
                              </div>
                              {item.product?.sku && (
                                <p className="text-xs text-slate-500 font-montserrat mt-1">
                                  SKU: {item.product.sku}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-600 font-montserrat">
                          {pkg.product || 'Sin detalles de producto'}
                        </p>
                      </div>
                    )}

                    {pkg.items && pkg.items.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-700 font-montserrat">
                            Total productos:
                          </span>
                          <span className="text-lg font-bold text-brand-primary font-montserrat">
                            {pkg.items.reduce((sum, item) => sum + item.quantity, 0)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* COD summary */}
          {delivery.charge > 0 && packages.length > 0 && (
            <div className="mt-6 bg-gradient-to-r from-brand-gold/10 to-brand-primary/10 border border-brand-gold/30 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <DollarSign className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                    Pago contra entrega
                  </h3>
                  <p className="text-sm text-slate-700 font-montserrat mb-3">
                    El conductor cobrará el monto total al momento de la entrega.
                  </p>
                  <div className="bg-white rounded-lg p-3 border border-brand-gold/30">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-slate-700 font-montserrat">
                        Monto total a pagar:
                      </span>
                      <span className="text-2xl font-bold text-brand-primary font-montserrat">
                        S/ {delivery.charge.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── ADS: Below packages, after payment summary ── */}
          {/* Natural position — user has finished reading delivery info */}
          {!loading && (
            <div className="mt-6 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <RecommendedProductsRow
                deliveryId={delivery.id}
                placement="my_delivery"
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default MyDelivery;